import { apiFetch } from '@/lib/api/fetch'
import { GraphqlError } from '@/lib/api/error'
import type { TypedDocumentString } from '@/graphql/generated'

interface GraphqlEnvelope<TData> {
  data?: TData
  errors?: { message: string; path?: (string | number)[] }[]
}

/**
 * Run a pg_graphql operation through the API's `/graphql` handler.
 *
 * Deliberately NOT Supabase's own `/graphql/v1`. The handler in packages/api
 * does three things a browser cannot: it sets the singular
 * `request.jwt.claim.sub` GUC the custom IAM reads, primes
 * `iam.cache_access_statements()`, and scopes with `SET LOCAL ROLE authenticated`
 * inside one transaction. Bypassing it would bypass the authorization model.
 *
 * Documents are generated as strings rather than ASTs, so there is nothing to
 * print at runtime and the `graphql` package never reaches the browser bundle.
 *
 * GraphQL-over-HTTP answers 200 even when the operation failed, so `errors` has
 * to be checked separately from the status — `apiFetch` cannot see it.
 */
export async function graphqlRequest<TData, TVariables>(
  document: TypedDocumentString<TData, TVariables>,
  variables: TVariables,
  init?: { signal?: AbortSignal },
): Promise<TData> {
  const envelope = await apiFetch<GraphqlEnvelope<TData>>('/graphql', {
    method: 'POST',
    body: {
      query: document.toString(),
      variables: variables ?? {},
    },
    signal: init?.signal,
  })

  if (envelope.errors?.length) {
    throw new GraphqlError(envelope.errors[0].message, envelope)
  }

  if (!envelope.data) {
    throw new GraphqlError('GraphQL response carried no data', envelope)
  }

  return envelope.data
}
